import { useState, useMemo } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { 
  Bell, 
  Heart, 
  MessageCircle, 
  Sparkles, 
  Video, 
  Globe,
  Check,
  CheckCheck,
  ArrowLeft,
  Filter,
  Trash2,
  UserPlus,
  Zap,
  Unlock,
  XCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SwipeableNotification } from "@/components/notifications/SwipeableNotification";
import { toast } from "@/hooks/use-toast";

type FilterType = "all" | "likes" | "matches" | "messages" | "location" | "portal";

const FILTER_OPTIONS: { value: FilterType; label: string; icon: React.ReactNode; types: string[] }[] = [
  { value: "all", label: "All", icon: <Filter className="w-3.5 h-3.5" />, types: [] },
  { value: "likes", label: "Likes & Pops", icon: <Heart className="w-3.5 h-3.5" />, types: ["like", "pop", "super_pop"] },
  { value: "matches", label: "Matches", icon: <Heart className="w-3.5 h-3.5 fill-current" />, types: ["match"] },
  { value: "messages", label: "Messages", icon: <MessageCircle className="w-3.5 h-3.5" />, types: ["message", "video_call"] },
  { value: "location", label: "Location", icon: <Globe className="w-3.5 h-3.5" />, types: ["location_join", "join_request"] },
  { value: "portal", label: "Portal", icon: <Zap className="w-3.5 h-3.5" />, types: ["portal_invite_received", "portal_invite_accepted", "portal_invite_declined", "portal_capsule_received", "portal_message", "portal_chat_unlocked", "portal_closed"] },
];

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "like":
      return <Heart className="w-5 h-5 text-pink-500" />;
    case "pop":
    case "super_pop":
      return <Sparkles className="w-5 h-5 text-pop" />;
    case "match":
      return <Heart className="w-5 h-5 text-primary fill-primary" />;
    case "message":
      return <MessageCircle className="w-5 h-5 text-blue-500" />;
    case "video_call":
      return <Video className="w-5 h-5 text-green-500" />;
    case "location_join":
      return <Globe className="w-5 h-5 text-cyan-500" />;
    case "join_request":
      return <UserPlus className="w-5 h-5 text-amber-500" />;
    // Portal notifications
    case "portal_invite_received":
    case "portal_invite_accepted":
      return <Zap className="w-5 h-5 text-violet-500" />;
    case "portal_invite_declined":
    case "portal_closed":
      return <XCircle className="w-5 h-5 text-muted-foreground" />;
    case "portal_capsule_received":
      return <Sparkles className="w-5 h-5 text-violet-500" />;
    case "portal_message":
      return <MessageCircle className="w-5 h-5 text-violet-500" />;
    case "portal_chat_unlocked":
      return <Unlock className="w-5 h-5 text-green-500" />;
    default:
      return <Bell className="w-5 h-5 text-muted-foreground" />;
  }
};

const getNotificationRoute = (type: string, data: any) => {
  switch (type) {
    case "match":
    case "message":
      return "/matches";
    case "like":
    case "pop":
    case "super_pop":
      return "/pop-balloon";
    case "location_join":
      return data?.location_type === "arena" ? "/live-arenas" : "/teleport";
    case "join_request":
      return "/love-exhibition";
    // Portal notifications
    case "portal_invite_received":
    case "portal_invite_accepted":
    case "portal_invite_declined":
      return "/portal";
    case "portal_capsule_received":
    case "portal_message":
    case "portal_chat_unlocked":
    case "portal_closed":
      return data?.portal_id ? `/portal/${data.portal_id}` : "/portal";
    default:
      return "/";
  }
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification, clearAllRead } = useNotifications();
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");

  const filteredNotifications = useMemo(() => {
    if (activeFilter === "all") return notifications;
    const filterOption = FILTER_OPTIONS.find(f => f.value === activeFilter);
    if (!filterOption) return notifications;
    return notifications.filter(n => filterOption.types.includes(n.type));
  }, [notifications, activeFilter]);

  const readCount = useMemo(() => {
    return notifications.filter(n => n.is_read).length;
  }, [notifications]);

  const handleNotificationClick = async (notification: any) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    const route = getNotificationRoute(notification.type, notification.data);
    navigate(route);
  };

  const handleDelete = (notificationId: string) => {
    const { undo } = deleteNotification(notificationId);
    toast({
      title: "Notification deleted",
      description: "This action can be undone",
      duration: 5000,
      action: (
        <button
          onClick={undo}
          className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Undo
        </button>
      ),
    });
  };

  const handleClearAllRead = async () => {
    await clearAllRead();
    toast({
      title: "Cleared all read notifications",
      duration: 2000,
    });
  };

  return (
    <div className="min-h-screen sky-gradient pb-24">
      <header className="sticky top-0 bg-card/95 backdrop-blur-xl border-b border-border/50 z-30 px-4 py-4 shadow-sm">
        <div className="flex items-center justify-between animate-fade-in">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 transition-colors active:scale-95"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Notifications</h1>
          <div className="w-9" />
        </div>
        <div className="flex items-center gap-4 mt-2 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm text-primary font-medium flex items-center gap-1 hover:opacity-80 transition-opacity active:scale-95"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all as read ({unreadCount})
            </button>
          )}
          {readCount > 0 && (
            <button
              onClick={handleClearAllRead}
              className="text-sm text-destructive font-medium flex items-center gap-1 hover:opacity-80 transition-opacity active:scale-95"
            >
              <Trash2 className="w-4 h-4" />
              Clear read ({readCount})
            </button>
          )}
        </div>
      </header>

      {/* Filter chips */}
      <div className="sticky top-[73px] bg-card/95 backdrop-blur-xl z-20 px-4 py-3 border-b border-border/50 overflow-x-auto">
        <div className="flex gap-2 animate-slide-up">
          {FILTER_OPTIONS.map((filter, index) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200",
                "hover:scale-105 active:scale-95",
                activeFilter === filter.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted/80 text-muted-foreground hover:bg-muted"
              )}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {filter.icon}
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 max-w-lg mx-auto">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div 
                key={i} 
                className="flex items-start gap-3 p-4 bg-card/50 rounded-xl animate-pulse"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-10 h-10 rounded-full bg-muted/60 shimmer-bg" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-muted/60 rounded" />
                  <div className="h-3 w-1/2 bg-muted/40 rounded" />
                  <div className="h-3 w-1/4 bg-muted/30 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-16 animate-fade-in-up">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
              <Bell className="w-10 h-10 text-muted-foreground/50 animate-float" />
            </div>
            <p className="text-lg font-medium text-muted-foreground">
              {activeFilter === "all" ? "No notifications yet" : `No ${FILTER_OPTIONS.find(f => f.value === activeFilter)?.label.toLowerCase()} notifications`}
            </p>
            <p className="text-sm text-muted-foreground/70 mt-2">
              {activeFilter === "all" 
                ? "When someone likes, pops, or matches with you, you'll see it here"
                : "Try selecting a different filter"}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-3 px-1 animate-fade-in">
              Swipe left to delete
            </p>
            {filteredNotifications.map((notification, index) => (
              <SwipeableNotification 
                key={notification.id}
                onDelete={() => handleDelete(notification.id)}
              >
                <button
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full p-4 text-left transition-all duration-200 rounded-xl",
                    "flex items-start gap-3 hover-lift press-scale",
                    "animate-fade-in-up",
                    notification.is_read
                      ? "bg-card/50 hover:bg-card/70"
                      : "bg-card border-l-4 border-l-pop shadow-sm hover:shadow-md"
                  )}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div
                    className={cn(
                      "p-2 rounded-full shrink-0 transition-all duration-200",
                      notification.is_read ? "bg-muted" : "bg-pop/10"
                    )}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={cn(
                          "font-medium line-clamp-1",
                          !notification.is_read && "text-foreground"
                        )}
                      >
                        {notification.title}
                      </p>
                      {notification.is_read && (
                        <Check className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                    </div>
                    {notification.body && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                        {notification.body}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </button>
              </SwipeableNotification>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
